import AgencyConnect from '@/components/agent-apply/AgencyConnect';
import { jsonForHtml } from '@/lib/json-for-html';
import { AgentApplyForm } from '@/components/agent-apply/AgentApplyForm';
import { AgentApplyModal } from '@/components/agent-apply/AgentApplyModal';
import AgentEconomics from '@/components/agent-apply/AgentEconomics';
import DemoVideoStrip from '@/components/agent-apply/DemoVideoStrip';
import HowItWorks from '@/components/agent-apply/HowItWorks';
import { ALISHA_PHOTO } from '@/components/agent-apply/le-miami-photos';
import PlatformTrust from '@/components/agent-apply/PlatformTrust';
import SectionNav from '@/components/agent-apply/SectionNav';
import SymposiumBand from '@/components/agent-apply/SymposiumBand';
import WhatYouGet from '@/components/agent-apply/WhatYouGet';
import WhoWeWant from '@/components/agent-apply/WhoWeWant';
import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { usePage } from '@inertiajs/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BedDouble, Car, CarTaxiFront, ChevronDown, Plane, Play, Ship, Umbrella, Users } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

/* ------------------------------------------------------------------ */
/*  Demo video — swappable until the Phase 2 admin uploader lands.      */
/*  Set DEMO_VIDEO_URL to the transcoded CDN mp4 and DEMO_POSTER_URL to */
/*  its poster frame. Empty videoUrl renders a branded placeholder.     */
/* ------------------------------------------------------------------ */

const DEMO_VIDEO_URL = 'https://cdnweb.luxrallytravel.com/videos/become-an-agent-demo-1781755139.mp4';
const DEMO_POSTER_URL: string | undefined = 'https://cdnweb.luxrallytravel.com/images/become-an-agent-demo-poster-1781742152.jpg';

// Full-bleed hosts image band below the hero (Image Library: "David Eisen-3").
const HOSTS_IMAGE_URL = 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/06/8031c3b9-a408-4494-9039-65f37d78665b.jpg';


// Full-bleed Symposium band image (Image Library folder "Symposium", 2400px).
const SYMPOSIUM_HERO = {
    src: 'https://cdnweb.luxrallytravel.com/images/become-an-agent-symposium-hero-1781755256.jpg',
    alt: 'The LuxRally Travel team gathered at the annual Symposium in Miami, 2025',
};

// Headline scale metrics shown under the demo player (source: sales one-pager).
const DEMO_STATS = [
    { value: '1', label: 'Platform — search, book, invoice, track' },
    { value: '180K+', label: 'Direct hotel contracts' },
    { value: '20+', label: 'Wholesalers, plus GDS' },
    { value: '17%', label: 'Avg. yield on NET rates' },
];

// Section anchors for the hero index + sticky subnav (top-to-bottom page order).
const PAGE_SECTIONS = [
    { id: 'who', label: 'Who We Want' },
    { id: 'how', label: 'How It Works' },
    { id: 'demo', label: 'The Demo' },
    { id: 'numbers', label: 'The Numbers' },
    { id: 'support', label: 'Support' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'faq', label: 'FAQ' },
];

// What FastBook books today (Groups is new; Cruise launches Q2 2026).
const FASTBOOK_ITEMS = [
    { icon: BedDouble, label: 'Hotels' },
    { icon: Plane, label: 'Air' },
    { icon: Car, label: 'Cars' },
    { icon: CarTaxiFront, label: 'Transfers' },
    { icon: Umbrella, label: 'Insurance' },
    { icon: Users, label: 'Groups', badge: 'New' },
    { icon: Ship, label: 'Cruise', badge: 'Soon' },
];

/* ------------------------------------------------------------------ */
/*  Animation tokens                                                    */
/* ------------------------------------------------------------------ */

const EASE = [0.22, 1, 0.36, 1] as const;

const containerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: EASE },
    },
};

/* ------------------------------------------------------------------ */
/*  FAQ data                                                           */
/* ------------------------------------------------------------------ */

const FAQ_ITEMS: { q: string; a: ReactNode }[] = [
    {
        q: 'Who\u2019s behind LuxRally?',
        a: (
            <>
                <p>
                    David Eisen — our CEO and co-founder — started LuxRally out of lived experience, not industry experience. Before founding the
                    agency, he and his team ran exotic car rallies: multi-day events at Forbes Five-Star properties, buying out Michelin-starred
                    restaurants, operating inside the social world of ultra-high-net-worth travelers.
                </p>
                <p className="mt-3">
                    When he became a travel agent, he worked differently. No ten-page PDFs, no performative busywork, no service fees. He communicated
                    the way his clients actually communicated — directly, by text, in plain language — and could get someone booked within twenty
                    minutes of first contact.
                </p>
                <p className="mt-3">
                    He also came from an unusual background: years running cybersecurity for defense contractors. That discipline shaped the
                    infrastructure every LuxRally agent uses today — the booking platform, the CRM, the client data systems. He built them himself,
                    and he still uses them daily.
                </p>
                <p className="mt-3">
                    Courtney Eisen — our CMO and co-founder — launched LuxRally alongside David in 2022. Her background is in entrepreneurship and
                    marketing, and she codes alongside him, maintaining a separate marketing-technology codebase built to help our agents grow their
                    business.
                </p>
            </>
        ),
    },
    {
        q: 'How does the commission structure work?',
        a: (
            <>
                <p>Commission splits begin at 80/20 (you keep 80%) and scale to 90/10 after $100K in annual earned commissions.</p>
            </>
        ),
    },
    {
        q: 'Why don\u2019t LuxRally agents charge service fees?',
        a: (
            <>
                <p>Most host agencies treat service fees as a revenue stream — a planning fee here, a research fee there. We ban the practice.</p>
                <p className="mt-3">
                    The reasoning is economic, not ideological. Every friction point at the start of a client relationship reduces conversion. The
                    moment you ask someone to pay before they&rsquo;ve received value, some percentage won&rsquo;t cross the barrier. In our system,
                    where commissions are already substantial, a planning fee is noise compared to what a captured booking is worth.
                </p>
                <p className="mt-3">
                    Many advisors charge a fee for a simple reason: building a quote and handling the admin can take hours. Our system removes that
                    burden — quoting, invoicing, and the busywork are streamlined, so putting a polished quote together takes minutes, not an
                    afternoon. When the work isn&rsquo;t painful, charging to do it stops making sense.
                </p>
                <p className="mt-3">
                    Removing the fee also changes how clients relate to their agent. An advisor who doesn&rsquo;t charge to talk to you is an advisor
                    worth talking to. That goodwill compounds — clients refer more freely, return more readily, and treat the relationship as a
                    partnership rather than a transaction.
                </p>
            </>
        ),
    },
    {
        q: 'What is FastBook?',
        a: (
            <>
                <p>FastBook is our AI-powered, proprietary booking infrastructure, built entirely in-house.</p>
                <p className="mt-3">
                    It aggregates hundreds — often thousands — of Direct and NET rates for a single property within seconds. Beyond hotels, FastBook
                    integrates flights, tours, car transfers, car rentals, and travel insurance directly into one platform — with cruise booking
                    coming soon. You can use it from your phone, computer, or tablet.
                </p>
                <p className="mt-3">Key capabilities:</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>
                        <strong className="text-[#F5F5F5]/75">Commission Control</strong> — Adjust your commission up or down on NET rates to close
                        more deals or maximize margin, while still saving clients money vs. public rates.
                    </li>
                    <li>
                        <strong className="text-[#F5F5F5]/75">Automated Admin</strong> — Invoicing, commission tracking, client itineraries, payment
                        reminders, and cancellation tracking are handled automatically.
                    </li>
                    <li>
                        <strong className="text-[#F5F5F5]/75">Secure Client Profiles</strong> — Store names, passports, credit cards, and preferences.
                        Apply them to bookings with a click — no manual entry, no errors.
                    </li>
                    <li>
                        <strong className="text-[#F5F5F5]/75">Hotel List Search</strong> — Search by curated lists. Want to see which hotels David
                        sends clients to in Rome? Use his hotel list. Build and share your own.
                    </li>
                    <li>
                        <strong className="text-[#F5F5F5]/75">Reminders</strong> — Final payment dates, cancellation deadlines, passport renewals, and
                        custom reminders for every client and booking.
                    </li>
                </ul>
                <p className="mt-3">
                    FastBook also features built-in AI tools to help you research destinations, draft client communications, and work more
                    efficiently. Because we built it in-house, we ship improvements within days based on agent feedback. Additional features are
                    covered in the Systems course.
                </p>
                <button
                    type="button"
                    onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="lux-caps lux-caps-sm mt-4 inline-flex items-center gap-2 text-[#F5F5F5]/70 transition-colors hover:text-[#F5F5F5]"
                >
                    <Play className="h-3 w-3 fill-current" strokeWidth={1.5} />
                    Watch the demo
                </button>
            </>
        ),
    },
    {
        q: 'How secure is the platform that holds my clients\u2019 data?',
        a: (
            <>
                <p>
                    Most agencies treat security as a compliance checkbox. We treat it as an operational discipline — because the person who built our
                    systems came from a world where inadequate security has real consequences.
                </p>
                <p className="mt-3">
                    David&rsquo;s background running cybersecurity for defense contractors shaped how every system was architected from the ground up.
                    PCI compliance is the floor, not the ceiling. Beyond it:
                </p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>A deliberately minimal attack surface — fewer entry points, fewer vulnerabilities</li>
                    <li>Segmented PII, so a breach in one area can&rsquo;t cascade across the system</li>
                    <li>Ongoing penetration testing and social engineering simulations</li>
                    <li>Two-factor authentication and SSO as standard</li>
                    <li>3DS verification on client card payments, where the client&rsquo;s bank supports it</li>
                </ul>
                <p className="mt-3">
                    For agents whose clients include executives, public figures, and individuals with significant wealth and public profiles, this
                    matters. Those clients trust their advisor with information that, in the wrong hands, creates genuine risk. Our infrastructure is
                    built to honor that trust.
                </p>
            </>
        ),
    },
    {
        q: 'I\u2019m already producing at another host agency. What would switching actually look like?',
        a: (
            <>
                <p>You&rsquo;ll work less, earn more, and your clients will get better rates and better service.</p>
                <p className="mt-3 rounded-lg border border-[#F5F5F5]/15 bg-[#0e2727] p-4">
                    <strong className="text-[#F5F5F5]">Fast-track onboarding.</strong> If you&rsquo;re already producing over $1M, you skip the
                    Industry Course and Practical Exam entirely. The Systems Course and Orientation take just 2–3 hours — and we can have you fully
                    active, credentials and all, within 24 hours.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Real rate visibility.</strong> You see actual NET and direct rates — not a marked-up number
                    with the real margin hidden behind it. On NET rates, you set your own margin.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Faster cash flow.</strong> NET rate commissions appear on your statement ten days after the
                    booking becomes non-refundable — not sixty days after the client travels. At volume, the timing difference has real cash-flow
                    implications.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Less admin.</strong> Invoicing, commission tracking, commission collection emails, payment
                    installments, and accounting reconciliation are automated or partially automated inside FastBook. Every hour recovered from admin
                    is an hour available for selling.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Better client protection.</strong> 3DS card verification (where the client&rsquo;s bank
                    supports it) and structured client profiles that auto-populate at checkout — no rekeying passport and payment details every
                    booking. The risk of a costly data-entry error drops sharply.
                </p>
                <p className="mt-3">
                    We also don&rsquo;t restrict supplier loyalty. You stay loyal to your clients, not to a preferred-partner list that&rsquo;s really
                    about override commissions for the host.
                </p>
                <p className="mt-3 text-[#F5F5F5]/55">
                    One thing to plan for: existing bookings usually can&rsquo;t be transferred between agencies, so we recommend reviewing your
                    current host&rsquo;s contract before you move. We&rsquo;re glad to help you think through the timing.
                </p>
            </>
        ),
    },
    {
        q: 'What does the in-house concierge team actually do?',
        a: (
            <>
                <p>
                    Our concierge team is hired from Forbes Five-Star properties. These are people who spent careers inside the world&rsquo;s best
                    hotels — navigating the unwritten rules, building relationships with on-property contacts, and developing instincts that come only
                    from years operating at that level.
                </p>
                <p className="mt-3">
                    Practically, that means knowing how to communicate a difficult client profile to a hotel in a way that gets results, convincing a
                    maître d&rsquo; at a fully-booked restaurant to find a table (usually with a minimum), and ensuring a complex multi-stop trip
                    comes together without the invisible friction that derails ordinary bookings.
                </p>
                <p className="mt-3">
                    Agents get access at no cost on qualifying luxury bookings. You submit a ticket with the trip details, introduce your client to
                    the concierge via a group chat or email, and the concierge works directly with them — on whatever channel they prefer (text,
                    phone, WhatsApp, Signal, email) — to lock in every detail.
                </p>
                <p className="mt-3">
                    For new agents especially, this changes the math. You can close the core booking — flights, hotels — and hand the orchestration
                    off to a team that specializes in exactly this. You don&rsquo;t have to pretend you&rsquo;ve run a hundred complex luxury
                    itineraries yourself. You just need to be honest:{' '}
                    <em>my agency has a team that specializes in this, and they&rsquo;re going to take care of you.</em>
                </p>
                <p className="mt-3">
                    For established agents, the concierge is leverage — hand off the low- and no-revenue work and keep your time on relationships and
                    sales. To be clear on scope: the concierge handles ancillary services like tours and transfers, plus non-commissionable work like
                    restaurant reservations and client-profile setup. They never book your flights, hotels, or cruises. And if they do book something
                    commissionable on your behalf — a tour or a transfer — that commission stays yours.
                </p>
            </>
        ),
    },
    {
        q: 'What does the training look like?',
        a: (
            <>
                <p>
                    Our training is structured, focused, and designed to prepare you for real client work — quickly and confidently. Each phase
                    follows a defined timeline. The courses are self-guided, but the pacing is intentional. If the timelines feel unrealistic, this
                    may not be the right season to launch your business.
                </p>
                <p className="mt-3">The program includes four parts:</p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Industry Course</strong> (30 days)
                    <br />
                    Hundreds of short lessons — two to five minutes each, threaded with quizzes — covering luxury brands, destinations, quoting
                    strategy, and closing techniques. Most agents do two hours a day over thirty days. Someone committed can move through it in two to
                    three weeks.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Practical Exam</strong> (within 7 days of completing the Industry Course)
                    <br />
                    You&rsquo;re given twenty distinct client scenarios to prepare for — then join a live video call where a LuxRally team member
                    plays the client. The catch: you don&rsquo;t know which of the twenty scenarios you&rsquo;ll be tested on until the roleplay
                    begins. You have to ask the right questions, read the situation, and perform. You get three attempts to pass, and the exam runs
                    once a week.
                </p>
                <p className="mt-3 text-[#F5F5F5]/55">
                    The randomness is the point. It replicates what actually happens in the field — where a UHNW client doesn&rsquo;t announce in
                    advance what kind of trip they want or what market they&rsquo;re asking about. Because the twenty scenarios are deliberately
                    diverse, agents who pass come out with a solid starter foundation for handling a wide range of real-world situations.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Systems Course</strong> (14 days)
                    <br />
                    Deep training on FastBook, accounting workflows, and day-to-day operations. Required for all agents — including experienced
                    advisors — as it covers our proprietary infrastructure.
                </p>
                <p className="mt-3">
                    <strong className="text-[#F5F5F5]/75">Orientation</strong>
                    <br />
                    We walk you through your Florida exemption filing, credential activation, system access, and everything required to begin booking.
                </p>
                <p className="mt-3">
                    If you&rsquo;re already producing over $1M a year (with documentation), you fast-track: skip the Industry Course and Practical
                    Exam entirely, and the version of the Systems Course you take covers only our proprietary tech — no relearning what you already
                    know. For advisors producing under $1M, we handle the required training case by case.
                </p>
            </>
        ),
    },
    {
        q: 'What are the production expectations?',
        a: (
            <>
                <p>This is a 1099 independent contractor position — you are starting your own business.</p>
                <p className="mt-3">Revenue minimums:</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>$2,500 in revenue during your first full quarter post-graduation</li>
                    <li>$5,000 per quarter thereafter</li>
                    <li>$50,000 annually</li>
                </ul>
            </>
        ),
    },
    {
        q: 'How does LuxRally handle seller-of-travel licensing?',
        a: (
            <>
                <p>
                    We handle all seller-of-travel licensing across the United States and process all client payments through the agency. You
                    don&rsquo;t need your own agency, attorneys, or bonds.
                </p>
                <p className="mt-3">
                    Without a host agency, an advisor would need to form their own travel agency, navigate legal requirements in every state where
                    they do business, hire attorneys, and post bonds that can run north of $100,000. It&rsquo;s expensive, time-consuming, and has
                    serious consequences if not handled properly.
                </p>
                <p className="mt-3">
                    With LuxRally, you start from a clean legal foundation — no gaps, no surprises, no personal liability exposure from an overlooked
                    filing in a state you didn&rsquo;t know required separate registration.
                </p>
                <p className="mt-3">
                    One filing is still yours to handle: each agent is responsible for their own Independent Agent Affidavit/Statement of Exemption
                    with the State of Florida ($51.50/year). We walk you through it step-by-step during Orientation. Our host agency credentials fully
                    cover you once it&rsquo;s filed.
                </p>
            </>
        ),
    },
    {
        q: 'How important is responsiveness?',
        a: (
            <>
                <p>Critical. Luxury clients choose advisors over call centers because they expect access.</p>
                <p className="mt-3">
                    When someone calls, you need to answer or get back to them as soon as possible. If you know you will be unavailable while a client
                    is actively traveling, collaborate with another agent or our in-house concierge to cover for you.
                </p>
                <p className="mt-3">Failure to respond — especially during travel — is incompatible with our standards.</p>
            </>
        ),
    },
    {
        q: 'What does the community actually feel like day-to-day?',
        a: (
            <>
                <p>
                    The infrastructure is built around a layered chat system: an Agent Lounge for open conversation, separate channels for deals and
                    travel advisories, and regional forums for agents in the same geographic area. Meetups form organically from there.
                </p>
                <p className="mt-3">
                    One distinctive feature is the agency-wide big-booking notifications feed. When an agent closes a significant booking, it shows up
                    in a shared feed — a real-time pulse of what the community is producing. It celebrates wins genuinely and creates a low-grade
                    ambient motivation that keeps the room energized.
                </p>
                <p className="mt-3">
                    The call culture is telling. We run weekly calls, and agents frequently stay on after the formal session ends — without the
                    LuxRally team — to talk shop, co-work, and catch up. That&rsquo;s not something you can manufacture. It happens because the
                    community actually likes each other.
                </p>
                <p className="mt-3">
                    The annual symposium is the center of gravity. Getting everyone in the same room does something that chat threads and video calls
                    can&rsquo;t — it creates a bond that carries back into everyday interactions. Sales awards are presented there as well.
                </p>
            </>
        ),
    },
    {
        q: 'How selective is the application process?',
        a: (
            <>
                <p>
                    We receive between 600 and 1,500 applications per month. The vast majority don&rsquo;t make it in — not because the bar is
                    arbitrary, but because the bar is real.
                </p>
                <p className="mt-3">
                    The single most important factor is network. Industry experience is not required. The right mind and the right network are.
                </p>
                <p className="mt-3">
                    A meaningful network isn&rsquo;t a headcount — it&rsquo;s proximity to wealth and the social awareness to move within it. Someone
                    who lives in a major city, belongs to a social club, or has a hobby that naturally attracts UHNW individuals has the raw material.
                    The question we ask isn&rsquo;t just whether someone knows wealthy people — it&rsquo;s whether they understand how wealth moves,
                    and whether they have the time and instinct to move with it.
                </p>
                <p className="mt-3">
                    If someone has the right network, real ambition, and zero travel background, we want them — provided they have the time to
                    complete the training program.
                </p>
            </>
        ),
    },
    {
        q: 'What\u2019s the Forbes Travel Guide endorsement actually worth?',
        a: (
            <>
                <p>
                    Forbes Travel Guide is to the hotel industry what Michelin is to restaurants — an external authority whose ratings carry real
                    weight and real consequences.
                </p>
                <p className="mt-3">
                    When a hotel sees a booking coming through a Forbes-endorsed agency, it changes the dynamic before anyone picks up the phone.
                    Hotels that have earned a Forbes rating don&rsquo;t want a bad client experience tied to a Forbes-endorsed agency finding its way
                    back to the guide. That accountability pressure is structural — properties are primed to perform before your client even checks
                    in.
                </p>
                <p className="mt-3">
                    When you represent LuxRally, you arrive at the property with institutional credibility you didn&rsquo;t have to earn on your own.
                </p>
            </>
        ),
    },
    {
        q: 'Are there any fees?',
        a: (
            <>
                <p>No subscription fees. No membership fees. No client-facing service fees.</p>
                <p className="mt-3">
                    The only cost is $51.50/year to the State of Florida for your Independent Agent Affidavit/Statement of Exemption.
                </p>
                <p className="mt-3">Our model is built on frictionless closing and long-term trust. We earn when you earn.</p>
            </>
        ),
    },
];

/* ------------------------------------------------------------------ */
/*  Inline FAQ Accordion                                               */
/* ------------------------------------------------------------------ */

function FaqAccordion() {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const shouldReduceMotion = useReducedMotion();

    const toggle = useCallback((i: number) => setOpenIndex((prev) => (prev === i ? null : i)), []);

    return (
        <div className="divide-y divide-[#F5F5F5]/10">
            {FAQ_ITEMS.map((item, i) => {
                const isOpen = openIndex === i;
                return (
                    <div key={i}>
                        <button
                            type="button"
                            onClick={() => toggle(i)}
                            className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors"
                            aria-expanded={isOpen}
                        >
                            <span className="lux-body lux-body-lg pr-4 !font-medium text-[#F5F5F5]/85">{item.q}</span>
                            <ChevronDown
                                className="h-4 w-4 shrink-0 text-[#F5F5F5]/40 transition-transform duration-300"
                                style={{
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        </button>
                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <motion.div
                                    initial={shouldReduceMotion ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={shouldReduceMotion ? { height: 'auto', opacity: 0 } : { height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3, ease: EASE }}
                                    className="overflow-hidden"
                                >
                                    <div className="lux-body lux-body-sm pb-5 leading-relaxed text-[#F5F5F5]/60">{item.a}</div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Structured data (JobPosting)                                       */
/* ------------------------------------------------------------------ */

const JOB_POSTING_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: 'Travel Agent — LuxRally Travel (Forbes Travel Guide Endorsed)',
    description:
        'Join LuxRally Travel, a Forbes Travel Guide endorsed agency. Free training, AI-powered booking technology (FastBook), mentorship from top-producing advisors, and commission splits starting at 80/20. No experience required. 1099 independent contractor position.',
    datePosted: '2026-02-27',
    employmentType: 'CONTRACTOR',
    hiringOrganization: {
        '@type': 'Organization',
        name: 'LuxRally Travel',
        sameAs: 'https://www.luxrallytravel.com',
    },
    jobLocationType: 'TELECOMMUTE',
    applicantLocationRequirements: {
        '@type': 'Country',
        name: 'US',
    },
};

/* ------------------------------------------------------------------ */
/*  Advisor testimonials (verbatim excerpts from hostagencyreviews.com)*/
/* ------------------------------------------------------------------ */

const TESTIMONIALS = [
    {
        quote: 'I was originally with Fora Travel and decided to switch over with LuxRally Travel after reading an article about LuxRally and David Eisen. His work ethic is very much in line with who I am and the service I strive to provide clients. LuxRally has so much to offer, I love their trainings, they have an amazing support team, a great platform that really gives us a competitive edge and not only is my commission split higher but so is my % rate.',
        name: 'Eileen T.',
        context: 'Advisor',
    },
    {
        quote: "From our proprietary booking system giving us an edge on tech, ease of collaboration, bi-monthly meetings with our CEO and CMO, innovative and extensive training program, our in house concierge that gives a truly high touch experience for our clients, to the fact that we don't have supplier loyalty — giving us access to the best rates around — all while charging zero fees to us as agents or to our clients.",
        name: 'Rachel G.',
        context: 'Advisor',
    },
    {
        quote: 'LuxRally has been an amazing company to work for. The educational materials provided and mentoring from other agents and staff is unheard of. Not to mention, there are no fees at all to utilize all their amazing resources.',
        name: 'Stefanie C.',
        context: 'Advisor',
    },
];

/* ------------------------------------------------------------------ */
/*  Dark-bg input classes (reused across fields)                       */
/* ------------------------------------------------------------------ */

const INPUT_CLS =
    'lux-body lux-body-sm border-[#F5F5F5]/20 bg-transparent text-[#F5F5F5] transition-colors duration-200 placeholder:text-[#F5F5F5]/35 focus-visible:border-[#F5F5F5]/40 focus-visible:ring-[#F5F5F5]/10';

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */

export default function AgentApply() {
    const shouldReduceMotion = useReducedMotion();
    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => setModalOpen(true), []);
    const closeModal = useCallback(() => setModalOpen(false), []);

    // The header "Apply to Join" CTA dispatches this event (ctaAction: 'custom').
    useEffect(() => {
        const handler = () => openModal();
        window.addEventListener('agent-apply:open', handler);
        return () => window.removeEventListener('agent-apply:open', handler);
    }, [openModal]);

    const { harReviews, seoTitle } = usePage<{ harReviews?: { count?: number; rating?: number }; seoTitle?: string }>().props;
    const harCount = harReviews?.count ?? 68;
    const harRating = harReviews?.rating ?? 5.0;
    const harRatingDisplay = Number.isInteger(harRating) ? `${harRating}.0` : harRating.toFixed(1);

    return (
        <PublicLayout
            headerConfig={{
                position: 'absolute',
                elevation: 'none',
                scrollBehavior: 'alwaysVisible',
                textColor: '#F5F5F5',
                background: 'transparent',
                // Replace the default "Get Started" pill with an apply CTA on this page only.
                showGetStarted: false,
                showCta: true,
                ctaPosition: 'left',
                ctaText: 'Apply Now',
                ctaTextMobile: 'Apply Now',
                hideCtaMobile: true,
                ctaAction: 'custom',
                ctaCustomEventName: 'agent-apply:open',
                ctaStyle: 'filled',
            }}
        >
            <MetaHead
                title={seoTitle || "Be part of travel's next chapter. | LuxRally Travel"}
                description="Join LuxRally Travel — a Forbes Travel Guide endorsed agency. Free training, AI-powered booking technology, mentorship from top producers, and commission splits starting at 80/20."
            >
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonForHtml(JOB_POSTING_SCHEMA) }} />
            </MetaHead>

            {/* Section subnav — sits directly under the main header */}
            <SectionNav sections={PAGE_SECTIONS} />

            {/* ============================================================ */}
            {/*  Section 1 — Hero                                            */}
            {/* ============================================================ */}
            <section id="hero" className="agent-px relative bg-[#0e2727] pt-10 pb-24 sm:pt-16 sm:pb-10">
                <div className="relative z-10 mx-auto grid max-w-6xl gap-12 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center lg:gap-16">
                    <motion.div
                        variants={containerVariants}
                        initial={shouldReduceMotion ? 'visible' : 'hidden'}
                        animate="visible"
                        className="max-w-2xl"
                    >
                        {/* Eyebrow kicker — the brand promise sits above the headline; the headline
                            states the action. Tracked small-caps, muted, classic hero hierarchy. */}
                        <motion.span variants={itemVariants} className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/55">
                            For the Apex Travel Advisor
                        </motion.span>

                        <motion.h1 variants={itemVariants} className="lux-didot lux-didot-xl mb-8 text-[#F5F5F5]">
                            The Agency of the&nbsp;Future
                        </motion.h1>

                        {/* CTAs — both use the dark-teal bordered button family; Apply Now is filled, Watch Demo outlined */}
                        <motion.div variants={itemVariants} className="mb-10 flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                onClick={openModal}
                                className="lux-caps lux-caps-sm inline-flex items-center rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] sm:py-1"
                            >
                                Apply Now
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    document
                                        .getElementById('demo')
                                        ?.scrollIntoView({ behavior: shouldReduceMotion ? 'auto' : 'smooth', block: 'start' })
                                }
                                className="lux-caps lux-caps-sm group inline-flex items-center gap-2.5 pr-4 pl-1 text-[#F5F5F5] transition-colors hover:text-white"
                            >
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F5F5F5]/10 transition-transform duration-300 group-hover:scale-105">
                                    <Play className="ml-0.5 h-3 w-3 fill-[#F5F5F5] text-[#F5F5F5]" strokeWidth={1.5} />
                                </span>
                                Watch Demo
                            </button>
                        </motion.div>

                        <motion.p variants={itemVariants} className="lux-body lux-body-lg mb-10 max-w-xl text-[#F5F5F5]/75">
                            Free training. AI-powered booking technology. Direct hotel relationships. In-house concierge team. Mentorship from
                            leadership. No fees. We earn when you earn.
                        </motion.p>

                        <motion.div variants={itemVariants} className="mb-10 h-px w-16 bg-[#F5F5F5]/15" />

                        <motion.p variants={itemVariants} className="lux-body lux-body-lg max-w-lg text-[#F5F5F5]/75">
                            Whether you&rsquo;re an experienced travel advisor looking for better infrastructure or new to the industry with a strong network
                            and ambition — we built this for you.
                        </motion.p>
                    </motion.div>

                    {/* Hero image — Alisha/Courtney/Lexie. Desktop only; on mobile the LE Miami photos
                        live as a side-by-side pair under "Who We're Looking For". */}
                    <motion.figure
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                        className="mx-auto hidden w-full max-w-sm lg:mx-0 lg:block lg:max-w-none"
                    >
                        <img
                            src={ALISHA_PHOTO.src}
                            alt={ALISHA_PHOTO.alt}
                            className="editorial-filter w-full rounded-xl object-cover shadow-2xl ring-1 ring-[#F5F5F5]/10"
                        />
                        {/* Caption styled like our article image captions (uppercase, tracked, muted). */}
                        <figcaption className="mt-4 text-[11px] leading-[1.7] tracking-[0.2em] text-[#F5F5F5]/40 uppercase">
                            {ALISHA_PHOTO.caption}
                        </figcaption>
                    </motion.figure>
                </div>
            </section>

            {/* ============================================================ */}
            {/*  Section 1.5 — Agency owner / $1M+ advisor direct line       */}
            {/* ============================================================ */}
            <AgencyConnect />

            {/* ============================================================ */}
            {/*  Section 2 — Who We're Looking For                           */}
            {/* ============================================================ */}
            <WhoWeWant onApply={openModal} />

            {/* ============================================================ */}
            {/*  Section 3 — How It Works                                    */}
            {/* ============================================================ */}
            <HowItWorks />

            {/* ============================================================ */}
            {/*  Section 4 — Hosts image band (leads into the demo)          */}
            {/* ============================================================ */}
            <motion.section
                className="relative h-[460px] w-full overflow-hidden bg-[#0b2222] sm:h-[560px]"
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: EASE }}
            >
                <img
                    src={HOSTS_IMAGE_URL}
                    alt="Founder and CEO David Eisen addressing a private group of advisors and agency owners at LE Miami, June 2026"
                    className="editorial-filter h-full w-full object-cover object-center"
                />
                {/* Black scrim for caption legibility — never teal over a photo. */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pt-32 pb-10">
                    <div className="agent-px mx-auto max-w-5xl">
                        <span className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">Private event during LE Miami · June 2026</span>
                        <p className="lux-body lux-body-sm max-w-2xl leading-relaxed text-[#F5F5F5]/90">
                            Founder and CEO David Eisen unveils the LuxRally platform to a private room of top independent advisors and agency owners
                            — our first open look at technology we spent years building in stealth, now shared openly with the industry.
                        </p>
                    </div>
                </div>
            </motion.section>

            {/* ============================================================ */}
            {/*  Section 2.5 — Demo video strip                              */}
            {/* ============================================================ */}
            <DemoVideoStrip
                videoUrl={DEMO_VIDEO_URL}
                posterUrl={DEMO_POSTER_URL}
                eyebrow="Live · Proprietary Platform"
                heading="This isn't a mockup. It's our live system."
                subtitle="Proprietary technology no one else can match — booking a real trip in real time. Filmed early June 2026; we've since launched a full groups system (contracted, dynamic, and hybrid), with cruise landing shortly."
                stats={DEMO_STATS}
                bookingItems={FASTBOOK_ITEMS}
            />

            {/* ============================================================ */}
            {/*  Section 2.55 — One system, built right                      */}
            {/* ============================================================ */}
            <PlatformTrust />

            {/* ============================================================ */}
            {/*  Section 2.6 — Why advisors earn more (economics)            */}
            {/* ============================================================ */}
            <div id="numbers" className="scroll-mt-36">
                <AgentEconomics onRequestDemo={() => window.dispatchEvent(new CustomEvent('agency-connect:open'))} />
            </div>

            {/* ============================================================ */}
            {/*  Section 2.65 — What you get (infrastructure pillars)        */}
            {/* ============================================================ */}
            <div id="support" className="scroll-mt-36">
                <WhatYouGet />
            </div>

            {/* ============================================================ */}
            {/*  Section 2.7 — Symposium full-bleed band                     */}
            {/* ============================================================ */}
            <SymposiumBand imageUrl={SYMPOSIUM_HERO.src} alt={SYMPOSIUM_HERO.alt} />

            {/* ============================================================ */}
            {/*  Section 3 — Advisor Testimonials                            */}
            {/* ============================================================ */}
            <section id="reviews" className="agent-px scroll-mt-36 bg-[#0e2727] py-20 sm:py-28">
                <div className="mx-auto max-w-6xl">
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="mb-16"
                    >
                        <span className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/60">From Our Advisors</span>
                        <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">What our advisors say.</h2>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 gap-6 md:grid-cols-3"
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                    >
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={i}
                                className="group flex flex-col rounded-sm border border-[#F5F5F5]/[0.08] p-10 transition-colors duration-300 hover:border-[#F5F5F5]/15"
                            >
                                <div className="mb-4 flex gap-1" aria-label={`5 out of 5 stars`}>
                                    {Array.from({ length: 5 }).map((_, s) => (
                                        <svg key={s} viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 text-[#F5F5F5]/40">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="lux-body lux-body-sm flex-1 leading-relaxed text-[#F5F5F5]/75">{t.quote}</p>
                                <p className="lux-caps lux-caps-sm mt-8 text-[#F5F5F5]/60">
                                    {t.name} — {t.context}
                                </p>
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        className="mt-10 flex items-center justify-center gap-3"
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3, ease: EASE }}
                    >
                        <span className="lux-body lux-body-sm text-[#F5F5F5]/60">
                            {harRatingDisplay} stars across {harCount} reviews on
                        </span>
                        <a
                            href="https://hostagencyreviews.com/hosts/luxrally-travel"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block transition-opacity hover:opacity-75"
                        >
                            <img src="/har-logo.png" alt="Host Agency Reviews" className="h-7 w-auto object-contain opacity-50 brightness-0 invert" />
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/*  Section 6 — FAQ                                             */}
            {/* ============================================================ */}
            <section id="faq" className="agent-px scroll-mt-36 bg-[#0b2222] py-20 sm:py-28">
                <div className="mx-auto max-w-3xl">
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <span className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/60">Before You Apply</span>
                        <h2 className="lux-didot lux-didot-lg mb-10 text-[#F5F5F5]">Frequently Asked Questions</h2>
                    </motion.div>

                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                    >
                        <FaqAccordion />
                    </motion.div>

                    {/* Post-FAQ CTA */}
                    <motion.div
                        className="mt-10 flex items-center gap-3"
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
                    >
                        <span className="lux-body lux-body-sm text-[#F5F5F5]/60">Questions answered?</span>
                        <button
                            type="button"
                            onClick={openModal}
                            className="lux-caps lux-caps-sm text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            Apply Now
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/*  Section 7 — Application Form                                */}
            {/* ============================================================ */}
            <div className="agent-px bg-[#0b2222]">
                <hr className="mx-auto max-w-3xl border-t border-[#F5F5F5]/10" />
            </div>
            <section className="agent-px bg-[#0b2222] py-20 sm:py-28">
                <AgentApplyForm />
            </section>

            {/* Page-takeover popup form — triggered by the CTAs throughout
                the page. Mirrors the InquiryModal pattern. */}
            <AgentApplyModal open={modalOpen} onClose={closeModal} />
        </PublicLayout>
    );
}
